"use client"

import { useEffect } from "react"
import { isNativeApp } from "@/lib/native"

/**
 * Keyboard + visual-viewport insets, for browsers.
 *
 * NativeShell publishes `--keyboard-h` inside the iOS/Android shell from
 * Capacitor's keyboardWillShow. On the mobile web there is no such event, so
 * the var stayed 0 and every `fixed bottom-0` surface — the BottomSheet above
 * all — behaved as if the keyboard did not exist.
 *
 * What goes wrong without this, on iOS Safari: opening the keyboard does not
 * shrink the layout viewport. It shrinks the VISUAL viewport and scrolls it,
 * while `position: fixed` stays pinned to the layout viewport. So the sign-up
 * sheet's lower half (the fields you are typing into) ends up behind the
 * keyboard, and the overlay no longer reaches the top of what you can see —
 * which is how the page behind showed through above the sheet.
 *
 * The three vars are all read off `window.visualViewport`:
 *   --keyboard-h  how much of the layout viewport's bottom is covered
 *                 (keyboard, accessory bar), so bottom-anchored UI can sit on
 *                 top of it.
 *   --vv-top      how far the visual viewport has scrolled down inside the
 *                 layout viewport, so a full-bleed overlay can start there.
 *   --vv-height   the height actually visible.
 *
 * Mounted once in the root layout, beside NativeShell. Inert in the native
 * shell (NativeShell owns --keyboard-h there) and on any browser without the
 * VisualViewport API, where every consumer falls back to its 0px default.
 */
export function ViewportInsets() {
  useEffect(() => {
    if (isNativeApp()) return
    const vv = window.visualViewport
    if (!vv) return

    const root = document.documentElement
    let frame = 0

    const apply = () => {
      frame = 0
      // Rounded down: a fractional pixel of inset is not worth a repaint, and
      // iOS reports sub-pixel heights while the keyboard animates.
      const bottomInset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      root.style.setProperty("--keyboard-h", `${bottomInset}px`)
      root.style.setProperty("--vv-top", `${Math.max(0, Math.round(vv.offsetTop))}px`)
      root.style.setProperty("--vv-height", `${Math.round(vv.height)}px`)
    }

    // Coalesce to one write per frame: iOS fires resize and scroll together,
    // many times over, for the length of the keyboard animation.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(apply)
    }

    apply()
    vv.addEventListener("resize", schedule)
    vv.addEventListener("scroll", schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      vv.removeEventListener("resize", schedule)
      vv.removeEventListener("scroll", schedule)
      root.style.removeProperty("--keyboard-h")
      root.style.removeProperty("--vv-top")
      root.style.removeProperty("--vv-height")
    }
  }, [])

  return null
}
